import React, { useState, useMemo, useEffect, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  Input,
  Form,
  FormGroup,
  Label,
  Button,
  Card,
  CardBody,
  Col,
  Row,
  FormFeedback,
  CardTitle,
  Badge,
} from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { updateSurvey } from "../../api/survey"
import { useDispatch, useSelector } from "react-redux"
import TableContainer from "components/Common/TableContainer"
import { getSurveyQuestions, getSurvey } from "store/survey/actions"
import Swal from "sweetalert2"

const EditSurvey = () => {
  const { id: stringId } = useParams()
  const id = parseInt(stringId, 10)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const inputRef = useRef(null)

  document.title = "Edit Survey"

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editingQuestion, setEditingQuestion] = useState(null)
  const [showAddQuestion, setShowAddQuestion] = useState(false)

  const { surveyQuestions, survey } = useSelector(state => ({
    surveyQuestions: state.Surveys.surveyQuestions,
    survey: state.Surveys.survey,
  }))

  // Survey form state
  const [formValues, setFormValues] = useState({
    title: "",
    description: "",
  })

  // Question form state
  const [questionFormValues, setQuestionFormValues] = useState({
    text: "",
    type: "",
    options: ["", ""],
    mandatory: "false",
  })

  const [formErrors, setFormErrors] = useState({})

  // Load survey and questions on mount
  useEffect(() => {
    if (id) {
      dispatch(getSurvey(id))
      dispatch(getSurveyQuestions({ page: 0, surveyId: id, size: 20 }))
    }
  }, [id, dispatch])

  // Update form values when survey loads
  useEffect(() => {
    if (survey) {
      setFormValues({
        title: survey.title || "",
        description: survey.description || "",
      })
    }
  }, [survey])

  // Validation functions
  const validateSurveyForm = () => {
    const errors = {}
    if (!formValues.title.trim()) errors.title = "Title is required"
    if (!formValues.description.trim())
      errors.description = "Description is required"
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const validateQuestionForm = () => {
    const errors = {}
    if (!questionFormValues.text.trim())
      errors.text = "Question text is required"
    if (!questionFormValues.type) errors.type = "Question type is required"

    if (questionFormValues.type !== "Open") {
      const validOptions = questionFormValues.options.filter(option =>
        option.trim()
      )
      if (validOptions.length < 2) {
        errors.options = "At least 2 options are required"
      }
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Handle survey update
  const handleSurveyUpdate = async event => {
    event.preventDefault()

    if (!validateSurveyForm()) return

    setIsSubmitting(true)

    try {
      const surveyData = {
        title: formValues.title.trim(),
        description: formValues.description.trim(),
      }

      await updateSurvey(id, surveyData)
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Survey updated successfully",
      })
      setFormErrors({})
    } catch (error) {
      console.error("Error updating survey:", error)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error updating survey. Please try again.",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle question add/update
  const handleQuestionSubmit = async event => {
    event.preventDefault()

    if (!validateQuestionForm()) return

    try {
      const validOptions = questionFormValues.options.filter(option =>
        option.trim()
      )

      const questionData = {
        text: questionFormValues.text.trim(),
        type: questionFormValues.type,
        mandatory: questionFormValues.mandatory === "true",
        surveyId: id,
        options:
          questionFormValues.type === "Open"
            ? []
            : validOptions.map(title => ({
                Content: title.trim(),
                surveyId: id,
              })),
      }

      if (editingQuestion) {
        // Update existing question
        const response = await updateSurveyQuestion(
          editingQuestion.id,
          questionData
        )
        dispatch(updateSurveyQuestionSuccess(response.data))
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Question updated successfully",
        })
        setEditingQuestion(null)
      } else {
        // Create new question
        const response = await createSurveyQuestion(questionData)
        dispatch(setSurveyQuestionSuccess(response.data))
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Question added successfully",
        })
      }

      // Reset form
      setQuestionFormValues({
        text: "",
        type: "",
        options: ["", ""],
        mandatory: "false",
      })
      setFormErrors({})
      setShowAddQuestion(false)
    } catch (error) {
      console.error("Error with question:", error)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Question update failed. Please try again",
      })
    }
  }

  // Handle question edit
  const handleEditQuestion = question => {
    setEditingQuestion(question)
    setQuestionFormValues({
      text: question.text || "",
      type: question.type || "",
      mandatory: question.mandatory ? "true" : "false",
      options:
        question.options?.length > 0
          ? question.options.map(opt => opt.Content || opt.content || "")
          : ["", ""],
    })
    setShowAddQuestion(true)
  }

  // Handle question delete
  const handleDeleteQuestion = async questionId => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    })

    if (result.isConfirmed) {
      try {
        await deleteSurveyQuestion(questionId)
        dispatch(deleteSurveyQuestionSuccess(questionId))
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Question deleted successfully",
        })
      } catch (error) {
        console.error("Error deleting question:", error)
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to delete question. Please try again.",
        })
      }
    }
  }

  // Option handlers
  const handleAddOption = () => {
    setQuestionFormValues({
      ...questionFormValues,
      options: [...questionFormValues.options, ""],
    })
  }

  const handleDeleteOption = index => {
    if (questionFormValues.options.length <= 2) return

    const updatedOptions = [...questionFormValues.options]
    updatedOptions.splice(index, 1)
    setQuestionFormValues({
      ...questionFormValues,
      options: updatedOptions,
    })
  }

  const handleOptionTitleChange = (index, value) => {
    const updatedOptions = [...questionFormValues.options]
    updatedOptions[index] = value
    setQuestionFormValues({
      ...questionFormValues,
      options: updatedOptions,
    })
  }

  const handleInput = event => {
    event.target.style.height = "auto"
    event.target.style.height = `${event.target.scrollHeight}px`
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto"
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`
    }
  }, [formValues.description])

  const handleBack = () => {
    navigate("/surveys")
  }

  const cancelEdit = () => {
    setEditingQuestion(null)
    setShowAddQuestion(false)
    setQuestionFormValues({
      text: "",
      type: "",
      options: ["", ""],
      mandatory: "false",
    })
    setFormErrors({})
  }

  // Table columns
  const qcolumns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "id",
        Cell: ({ row }) => <span>Q{row.index + 1}</span>,
      },
      {
        Header: "QUESTION",
        accessor: "text",
      },
      {
        Header: "TYPE",
        accessor: "type",
        Cell: ({ value }) => (
          <Badge color={value === "Open" ? "info" : "primary"}>
            {value === "Multiple"
              ? "Multiple Choice"
              : value === "Single"
              ? "Single Choice"
              : "Open"}
          </Badge>
        ),
      },
      {
        Header: "MANDATORY",
        accessor: "mandatory",
        Cell: ({ value }) => (
          <Badge color={value ? "success" : "secondary"}>
            {value ? "Yes" : "No"}
          </Badge>
        ),
      },
    ],
    []
  )

  return (
    <React.Fragment>
      <div className="page-content">
        {/* Survey Details Card */}
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between mb-4">
              <Breadcrumbs title="Surveys" breadcrumbItem="Edit Survey" />
              <Button color="primary" onClick={handleBack}>
                <i className="bx bx-arrow-back me-1"></i> Back
              </Button>
            </div>

            <Form onSubmit={handleSurveyUpdate}>
              <FormGroup className="mb-3">
                <Label for="title">Survey Title</Label>
                <Input
                  type="text"
                  id="title"
                  value={formValues.title}
                  onChange={e => {
                    setFormValues({ ...formValues, title: e.target.value })
                    if (formErrors.title)
                      setFormErrors({ ...formErrors, title: null })
                  }}
                  invalid={!!formErrors.title}
                  placeholder="Enter survey title"
                />
                {formErrors.title && (
                  <FormFeedback>{formErrors.title}</FormFeedback>
                )}
              </FormGroup>

              <FormGroup className="mb-4">
                <Label for="description">Description</Label>
                <Input
                  type="textarea"
                  id="description"
                  innerRef={inputRef}
                  value={formValues.description}
                  onChange={e => {
                    setFormValues({
                      ...formValues,
                      description: e.target.value,
                    })
                    handleInput(e)
                    if (formErrors.description)
                      setFormErrors({ ...formErrors, description: null })
                  }}
                  invalid={!!formErrors.description}
                  style={{ overflow: "hidden", resize: "none" }}
                  placeholder="Describe your survey"
                />
                {formErrors.description && (
                  <FormFeedback>{formErrors.description}</FormFeedback>
                )}
              </FormGroup>

              <div className="text-center mb-4">
                <Button color="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Updating..." : "Update Survey"}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>

        {/* Questions Section */}
        <Card className="mt-4">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <CardTitle tag="h5" className="mb-0">
                Survey Questions ({surveyQuestions?.length || 0})
              </CardTitle>
              <Button
                color="success"
                onClick={() => setShowAddQuestion(true)}
                disabled={showAddQuestion}
              >
                <i className="bx bx-plus me-1"></i>
                Add Question
              </Button>
            </div>

            {/* Add/Edit Question Form */}
            {showAddQuestion && (
              <div className="bg-light p-4 rounded mb-4">
                <h5 className="mb-3">
                  {editingQuestion ? "Edit Question" : "Add New Question"}
                </h5>

                <Form onSubmit={handleQuestionSubmit}>
                  <Row>
                    <Col md={6}>
                      <FormGroup className="mb-3">
                        <Label for="text">Question</Label>
                        <Input
                          type="text"
                          id="text"
                          value={questionFormValues.text}
                          onChange={e => {
                            setQuestionFormValues({
                              ...questionFormValues,
                              text: e.target.value,
                            })
                            if (formErrors.text)
                              setFormErrors({ ...formErrors, text: null })
                          }}
                          invalid={!!formErrors.text}
                          placeholder="Enter your question"
                        />
                        {formErrors.text && (
                          <FormFeedback>{formErrors.text}</FormFeedback>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup className="mb-3">
                        <Label for="type">Type</Label>
                        <Input
                          type="select"
                          id="type"
                          value={questionFormValues.type}
                          onChange={e => {
                            setQuestionFormValues({
                              ...questionFormValues,
                              type: e.target.value,
                            })
                            if (formErrors.type)
                              setFormErrors({ ...formErrors, type: null })
                          }}
                          invalid={!!formErrors.type}
                        >
                          <option value="">Select type</option>
                          <option value="Open">Open</option>
                          <option value="Multiple">Multiple Choice</option>
                          <option value="Single">Single Choice</option>
                        </Input>
                        {formErrors.type && (
                          <FormFeedback>{formErrors.type}</FormFeedback>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup className="mb-3">
                        <Label for="mandatory">Mandatory</Label>
                        <Input
                          type="select"
                          id="mandatory"
                          value={questionFormValues.mandatory}
                          onChange={e => {
                            setQuestionFormValues({
                              ...questionFormValues,
                              mandatory: e.target.value,
                            })
                          }}
                        >
                          <option value="false">No</option>
                          <option value="true">Yes</option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Options */}
                  {questionFormValues.type &&
                    questionFormValues.type !== "Open" && (
                      <FormGroup className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <Label>Options</Label>
                          <Button
                            color="success"
                            size="sm"
                            type="button"
                            onClick={handleAddOption}
                          >
                            Add Option
                          </Button>
                        </div>

                        {questionFormValues.options.map((title, index) => (
                          <Row key={index} className="mb-2">
                            <Col md={10}>
                              <Input
                                type="text"
                                value={title}
                                onChange={e =>
                                  handleOptionTitleChange(index, e.target.value)
                                }
                                placeholder={`Option ${index + 1}`}
                                required
                              />
                            </Col>
                            <Col md={2}>
                              {questionFormValues.options.length > 2 && (
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleDeleteOption(index)}
                                >
                                  Delete
                                </Button>
                              )}
                            </Col>
                          </Row>
                        ))}
                      </FormGroup>
                    )}

                  <div className="text-center">
                    <Button color="success" type="submit" className="me-2">
                      {editingQuestion ? "Update Question" : "Add Question"}
                    </Button>
                    <Button
                      color="secondary"
                      type="button"
                      onClick={cancelEdit}
                    >
                      Cancel
                    </Button>
                  </div>
                </Form>
              </div>
            )}

            <TableContainer
              columns={qcolumns}
              data={surveyQuestions || []}
              isGlobalFilter={true}
              isAddOptions={false}
              customPageSize={10}
              isPagination={true}
              iscustomPageSizeOptions={true}
              tableClass="align-middle table-nowrap table-check table w-full"
              theadClass="table-light"
              paginationDiv="col-12"
              pagination="justify-content-left pagination pagination-rounded"
              showEdit={true}
              showDelete={true}
              handleEdit={row => handleEditQuestion(row.original)}
              handleDelete={handleDeleteQuestion}
              emptyMessage="No questions available."
            />
          </CardBody>
        </Card>
      </div>
    </React.Fragment>
  )
}

export default EditSurvey
